import type { GlobalThreatGroupAnalysisResult } from '@types';
import { localDateInputValue } from './localDateTime';

export type ThreatGroupIncident = NonNullable<GlobalThreatGroupAnalysisResult['recentActivities']>[number];

export interface ThreatGroupIncidentMonth {
    month: string;
    count: number;
    incidents: ThreatGroupIncident[];
    localCount: number;
    theaterCount: number;
    unclassifiedCount: number;
}

export function buildThreatGroupTimeline(activities: ThreatGroupIncident[] = [], now = new Date()): ThreatGroupIncidentMonth[] {
    const timeline: ThreatGroupIncidentMonth[] = [];
    const months = new Map<string, ThreatGroupIncidentMonth>();
    const today = localDateInputValue(now);
    for (let offset = 11; offset >= 0; offset--) {
        const month = localDateInputValue(new Date(now.getFullYear(), now.getMonth() - offset, 1)).slice(0, 7);
        const bucket = { month, count: 0, localCount: 0, theaterCount: 0, unclassifiedCount: 0, incidents: [] as ThreatGroupIncident[] };
        timeline.push(bucket);
        months.set(month, bucket);
    }

    for (const activity of activities) {
        const date = typeof activity?.date === 'string' ? activity.date.trim() : '';
        const match = /^(\d{4}-(?:0[1-9]|1[0-2]))(?:-(\d{2})(?:T.+)?)?$/.exec(date);
        if (!match)
            continue;
        const bucket = months.get(match[1]);
        if (!bucket)
            continue;
        if (match[2]) {
            const day = date.slice(0, 10);
            const parsed = new Date(`${day}T00:00:00Z`);
            if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== day || day > today ||
                (date.length > 10 && !Number.isFinite(Date.parse(date))))
                continue;
        }
        bucket.incidents.push(activity);
        bucket.count++;
        if (activity.scope === 'local')
            bucket.localCount++;
        else if (activity.scope === 'theater')
            bucket.theaterCount++;
        else
            bucket.unclassifiedCount++;
    }
    for (const bucket of timeline)
        bucket.incidents.sort((a, b) => b.date.trim().localeCompare(a.date.trim()));
    return timeline;
}

export function threatGroupTimelineDate(result: GlobalThreatGroupAnalysisResult): Date {
    const date = result.incidentCoverage?.endDate || result.assessmentDate;
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        const parsed = new Date(`${date}T12:00:00`);
        if (Number.isFinite(parsed.getTime()) && localDateInputValue(parsed) === date)
            return parsed;
    }
    return new Date();
}

export function threatGroupCoverageNote(result: GlobalThreatGroupAnalysisResult): string {
    const coverage = result.incidentCoverage;
    if (!coverage)
        return 'Incident search coverage was not recorded for this saved report. Regenerate the briefing to refresh regional research.';
    return (coverage.telemetryStatus === 'failed' ? 'GDELT retrieval failed; telemetry counts remain unknown. '
        : coverage.telemetryStatus === 'empty' ? 'No actor-linked GDELT records were retrieved. Independent source research determines the listed incidents. '
            : 'GDELT coded records supplied research leads; the chart counts only sourced incidents retained in the briefing. ')
        + coverage.limitations;
}
